// generateQuoteDocument.js
// Regenerates a quote's printable/emailable HTML from its logged "State JSON",
// without re-running any of the live calculator's pricing logic.
// Shared by QuotePage.jsx (public quote view) and QuoteLog.jsx (Send to Client).

import { generateQuoteHTML } from "./pdfTemplate";
import { resolveClientScope, scopeTextToHTML, isSectionedScope, scopeHasProtection } from "./scopeTemplates";
import { parseDate } from "./quoteLogger";
import { protectionGuaranteeHTML, protectionTermsSentence, getProtectionTier } from "./protectionTiers";
import { legalFooterLine, loadCompanySettings } from "./companyFooter";
import { AUTO_SERVICE_HOURS, autoStages, AUTO_STAGE_NAMES } from "./autoCatalogue";


// ─── Discount ─────────────────────────────────────────────────────────────
// The logged "Total (ex VAT)" is already the post-discount amount (that's
// what was actually charged). For percent/flat discounts we can reconstruct
// the subtotal from that final total + the saved discount config. For
// "target final price" discounts we can't — the saved final total IS the
// target, so the original subtotal is lost unless it was snapshotted at
// save time. Quotes saved since that snapshot was added carry
// commercial._discountSnapshot, which is used directly when present; older
// rows (pre-dating "target" mode, or any percent/flat quote saved before
// the snapshot existed) fall back to reconstructing it from the formula.
// Returns null when the quote has no discount at all.
export function resolveDiscount(commercial, finalPrice) {
  if (commercial?._discountSnapshot) {
    const { amount, subtotalExVat, label, reason } = commercial._discountSnapshot;
    if (amount > 0) return { subtotalExVat, amount, label: label || "", reason: reason || "" };
  }

  const type = commercial?.discountType;
  if (type === "target") return null; // no snapshot to recover this from
  const value = Number(commercial?.discountValue) || 0;
  if (!type || type === "none" || value <= 0) return null;
  let subtotalExVat, amount;
  if (type === "percent") {
    const pct = Math.min(Math.max(value, 0), 99.99);
    subtotalExVat = finalPrice / (1 - pct / 100);
    amount = subtotalExVat - finalPrice;
  } else {
    amount = value;
    subtotalExVat = finalPrice + amount;
  }
  return {
    subtotalExVat, amount,
    label: type === "percent" ? `${value}%` : "",
    reason: (commercial?.discountReason || "").trim(),
  };
}

const fmtGBP = n => "£" + Number(n || 0).toLocaleString("en-GB", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

// The quote's date as printed: its Date Created (dd/mm/yyyy) when known,
// otherwise today. Same format on the PDF and View Full Quote.
export function quoteDateStr(dateCreated) {
  return (parseDate(dateCreated) || new Date()).toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric" });
}

// The photo consent line only goes with the current preset ("Showcase vehicle
// rate…"), so older Showcase Project quotes don't gain it when reopened.
const needsShowcasePhotoLine = discount => !!discount && discount.amount > 0 && /^showcase vehicle rate/i.test(String(discount.reason || "").trim());
const SHOWCASE_PHOTO_LINE = "Showcase rate: accepting this quote confirms you’re happy for us to photograph and share the work, with your details and number plate kept private.";

// Short client wording per service for the "At a glance" Clean line.
const GLANCE_SERVICE = {
  underseelRemoval: "underseal and bitumen removal",
  fullUnderbody: "full underbody, wheels off",
  underbodyOnly: "floorpan and sills",
  wheelArches: "wheel arches",
  suspensionBrakes: "suspension and brakes",
  engineBay: "engine bay",
  drivetrain: "drivetrain and gearbox",
  exhaust: "exhaust system",
  fullExteriorDetail: "exterior detail preparation",
  concoursInterior: "concours interior preparation",
};

// "At a glance" box on page 1 of the Automotive quote: the stages that apply
// (Clean, Rust treatment, Protection - same names as the Scope of Works
// sections), an Add-ons row naming any detailing services ticked, and the
// protection system's one-line guarantee. The price and
// booking terms are said once, in the price block below it.
function autoGlanceHTML(state) {
  const esc = v => String(v).replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
  const st = autoStages(state);
  const N = AUTO_STAGE_NAMES;
  const tier = getProtectionTier(state.protection);
  const lower = l => l.charAt(0).toLowerCase() + l.slice(1);
  const clean = st.clean.map(k => GLANCE_SERVICE[k] || lower(AUTO_SERVICE_HOURS[k].label));
  const rows = [
    clean.length && [N.clean, `Dry ice clean: ${clean.join(", ")}`],
    st.rust && [N.rust, st.rust.product || lower(st.rust.label)],
    st.protection && [N.protection, st.protection],
    st.detailing.length && [N.addOns, st.detailing.join(", ")],
    tier?.glance && ["Guarantee", tier.glance],
  ].filter(Boolean);
  if (!rows.length) return "";
  const row = (k, v) => `<tr><td style="padding:3px 14px 3px 0;color:#5a6a7a;white-space:nowrap;vertical-align:top;">${esc(k)}</td><td style="padding:3px 0;color:#0e1520;">${esc(v)}</td></tr>`;
  return `<div class="glance">
    <div class="blktitle">At a glance</div>
    <table style="width:auto;font-size:12.5px;">${rows.map(([k, v]) => row(k, v)).join("")}</table>
  </div>`;
}

// The Scope of Works on page 2. A sectioned scope (saved since the numbered
// sections) already holds rust treatment, protection with its guarantee and
// the client-visible additional work and add-ons; an older free-text scope
// prints word for word, followed by the additional work, add-ons (detailing
// included) and guarantee it doesn't have.
function autoScopeHTML(state, clientScope) {
  const esc = v => String(v).replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
  const sectioned = isSectionedScope(clientScope);
  const st = autoStages(state);
  const additional = sectioned ? [] : st.additional;
  const addOns = sectioned ? [] : st.addOns;
  return `<div class="sec">
    <div class="sectitle">Scope of Works</div>
    <p class="method">Cleaning method: dry ice CO₂ blasting, with no chemicals, no moisture and nothing abrasive.</p>
    ${clientScope ? `<div class="scope-box">${scopeTextToHTML(clientScope)}</div>` : ""}
    ${[[AUTO_STAGE_NAMES.additional, additional], [AUTO_STAGE_NAMES.addOns, addOns]].filter(([, l]) => l.length).map(([name, l]) => `<div class="scope-box"><strong>${name}</strong><ul style="margin:4px 0 0;padding-left:20px;">${l.map(x => `<li>${esc(x)}</li>`).join("")}</ul></div>`).join("")}
  </div>
  ${sectioned && scopeHasProtection(clientScope) ? "" : protectionGuaranteeHTML(state.protection)}`;
}

// ─── Automotive client quote ─────────────────────────────────────────────────
// The ONE client-facing Automotive quote: the calculator's Print/Save PDF and
// View Full Quote / Send to Client both build it from the quote's saved state,
// so they can't show different content. Client-facing only - no labour
// hours, dry ice kg, operative count or estimator (those are on the internal
// job sheet, see jobSheet.js).
// discount: { subtotalExVat, amount, label, reason } or null.
// company: Company Settings for the legal footer (null = fallback values).
export function buildAutomotiveQuoteHTML({ refNo, dateStr, totalExVat, discount, state, logoSrc, company = null }) {
  const { client = {}, vehicle = {}, job = {}, protection, notes = {} } = state || {};
  const protLabel = autoStages(state || {}).protection;
  const clientScope = resolveClientScope(state) || "";
  const total = Number(totalExVat) || 0;
  const incVat = total * 1.2;
  // In-unit: the vehicle comes to our unit and goes on the lift, and the
  // balance is paid when it's collected. On-site (and quotes saved before
  // Job Location existed, treated as on-site): we bring the kit to the vehicle.
  const inUnit = job.jobLocation === "in-unit";

  return `<!DOCTYPE html>
<html>
<head>
  <meta charset="UTF-8"/>
  <meta name="viewport" content="width=device-width, initial-scale=1.0"/>
  <title>Vehicle Preservation Quote ${refNo}</title>
  <style>
    @import url('https://fonts.googleapis.com/css2?family=IBM+Plex+Sans:wght@400;500;600;700&display=swap');
    * { margin:0; padding:0; box-sizing:border-box; }
    body { font-family:'IBM Plex Sans',sans-serif; color:#0e1520; background:#fff; font-size:12px; line-height:1.6; }
    .page { padding:52px 56px; max-width:794px; margin:0 auto; }
    .hdr { display:flex; justify-content:space-between; align-items:center; margin-bottom:32px; padding-bottom:24px; border-bottom:1px solid #dde2ea; }
    .brand { font-size:20px; font-weight:700; color:#3a52cc; letter-spacing:-0.01em; }
    .brandSub { font-size:9px; color:#8a95a3; letter-spacing:0.15em; text-transform:uppercase; margin-top:3px; }
    .refblock { text-align:right; }
    .reflabel { font-size:9px; font-weight:600; letter-spacing:0.18em; text-transform:uppercase; color:#8a95a3; margin-bottom:4px; }
    .refval { font-size:16px; font-weight:700; color:#0e1520; }
    .refdate { font-size:11px; color:#8a95a3; margin-top:3px; }
    .badge { display:inline-block; background:#eef1fa; color:#1D3461; border-radius:3px; padding:1px 7px; font-size:9px; font-weight:700; letter-spacing:0.08em; text-transform:uppercase; margin-left:6px; }
    h1 { font-size:28px; font-weight:700; color:#0e1520; letter-spacing:-0.02em; line-height:1.1; margin-bottom:5px; }
    .sub { font-size:10px; font-weight:600; letter-spacing:0.2em; text-transform:uppercase; color:#8a95a3; margin-bottom:8px; }
    .taglinehdr { font-size:13px; font-style:italic; color:#5a6a7a; margin-bottom:28px; }
    .twocol { display:grid; grid-template-columns:1fr 1fr; gap:16px; margin-bottom:28px; }
    .blk { background:#f7f8fa; border-radius:6px; padding:16px 18px; }
    .blktitle { font-size:9px; font-weight:700; letter-spacing:0.18em; text-transform:uppercase; color:#8a95a3; margin-bottom:10px; }
    .blk p { font-size:12px; color:#0e1520; margin-bottom:2px; }
    .blk strong { font-weight:600; }
    .sec { margin-bottom:24px; }
    .sectitle { font-size:10px; font-weight:700; letter-spacing:0.18em; text-transform:uppercase; color:#0e1520; margin-bottom:10px; padding-bottom:8px; border-bottom:1px solid #dde2ea; }
    table { width:100%; border-collapse:collapse; font-size:12px; }
    th { background:#0e1520; color:#fff; padding:8px 14px; text-align:left; font-size:9px; letter-spacing:0.14em; text-transform:uppercase; font-weight:600; }
    td { padding:9px 14px; border-bottom:1px solid #eef0f5; color:#2a3444; vertical-align:top; }
    tr:last-child td { border-bottom:none; }
    .scope-box { background:#f7f8fa; border-left:3px solid #3a52cc; padding:14px 18px; font-size:12px; color:#2a3444; margin-bottom:24px; border-radius:0 6px 6px 0; line-height:1.7; }
    .pricesec { margin:28px 0; padding:24px 0; border-top:2px solid #0e1520; border-bottom:1px solid #dde2ea; }
    .pricemain { display:flex; justify-content:space-between; align-items:flex-start; margin-bottom:16px; }
    .pricelabel { font-size:9px; font-weight:600; letter-spacing:0.18em; text-transform:uppercase; color:#8a95a3; margin-bottom:6px; }
    .pricefig { font-size:44px; font-weight:700; color:#0e1520; letter-spacing:-0.03em; line-height:1; }
    .pricevat { font-size:11px; color:#8a95a3; margin-top:5px; }
    .priceterms { text-align:right; padding-top:8px; }
    .priceterms p { font-size:12px; color:#0e1520; font-weight:500; margin-bottom:2px; }
    .terms { font-size:10px; color:#8a95a3; line-height:1.8; padding-top:16px; border-top:1px solid #eef0f5; margin-top:8px; }
    .glance { border:1px solid #dde2ea; border-left:3px solid #3a52cc; border-radius:0 6px 6px 0; padding:14px 18px 12px; margin-bottom:24px; }
    .glance table { border-collapse:collapse; }
    .glance td { border-bottom:none; }
    /* The full detail under the At a glance box: smaller and lighter. */
    .detail { color:#5a6a7a; }
    .detail .sectitle { font-size:9px; color:#5a6a7a; }
    .detail .scope-box { font-size:11px; color:#5a6a7a; background:#fafbfc; border-left-color:#c5ccd8; }
    .detail table { font-size:11px; }
    .detail td { color:#5a6a7a; padding:6px 14px; }
    .detail p { color:#5a6a7a !important; }
    .footer { margin-top:28px; padding-top:12px; border-top:1px solid #dde2ea; font-size:9px; line-height:1.5; color:#aab0ba; text-align:center; break-inside:avoid; }
    .method { font-size:11.5px; color:#5a6a7a; margin:0 0 10px; }
    @media print { body { -webkit-print-color-adjust:exact; print-color-adjust:exact; } @page { margin:15mm 10mm; size:a4; } .page { padding:0 8px; } .hdr { margin-bottom:22px; padding-bottom:16px; } .pricesec { margin:20px 0; padding:18px 0; } .footer { margin-top:16px; break-inside:avoid; } .glance { break-inside:avoid; } .p2 { break-before:page; padding-top:4px; } .p2 .sec { margin-bottom:14px; } .p2 .sectitle { margin-bottom:6px; padding-bottom:5px; } .p2 th { padding:6px 12px; } .p2 td, .p2 .detail td { padding:4px 12px; } .p2 .scope-box { padding:10px 14px; margin-bottom:14px; line-height:1.55; } .terms { padding-top:10px; line-height:1.6; } .p2 .included td { padding:2px 12px; border-bottom:none; } .tagline { margin:14px 0 6px !important; } }
    @media screen and (max-width:600px) {
      .page { padding:24px 20px !important; }
      .hdr { flex-direction:column; align-items:flex-start; gap:16px; }
      .refblock { text-align:left !important; }
      .twocol { grid-template-columns:1fr !important; }
      .pricemain { flex-direction:column; gap:12px; }
      .priceterms { text-align:left !important; }
      .pricefig { font-size:32px !important; }
    }
  </style>
</head>
<body>
<div class="page">
  <div class="hdr">
    <div>
      <img src="${logoSrc}" style="height:38px;width:auto;" alt="Sublime Precision Cleaning"/>
    </div>
    <div class="refblock">
      <div class="reflabel">Quote Reference</div>
      <div class="refval">${refNo}</div>
      <div class="refdate">${dateStr} <span class="badge">Valid 30 days</span></div>
    </div>
  </div>
  <h1>Vehicle Preservation Quote</h1>
  <div class="sub">Dry Ice Blasting &amp; Underbody Protection</div>
  <div class="taglinehdr">Precision cleaning for vehicles that deserve it.</div>
  <div class="twocol">
    <div class="blk">
      <div class="blktitle">Client Details</div>
      ${client.name?`<p><strong>${client.name}</strong></p>`:""}
      ${client.email?`<p>${client.email}</p>`:""}
      ${client.phone?`<p>${client.phone}</p>`:""}
      ${job.workType?`<p><strong>Work Type:</strong> ${job.workType}</p>`:""}
    </div>
    <div class="blk">
      <div class="blktitle">Vehicle Details</div>
      ${vehicle.reg?`<p><strong>Registration:</strong> ${String(vehicle.reg).toUpperCase()}</p>`:""}
      ${(vehicle.make||vehicle.model)?`<p><strong>Make / Model:</strong> ${[vehicle.make, vehicle.model].filter(Boolean).join(" ")}</p>`:""}
      ${vehicle.year?`<p><strong>Year:</strong> ${vehicle.year}</p>`:""}
      ${vehicle.colour?`<p><strong>Colour:</strong> ${vehicle.colour}</p>`:""}
      ${vehicle.vehicleType?`<p><strong>Type:</strong> ${vehicle.vehicleType}</p>`:""}
    </div>
  </div>
  ${autoGlanceHTML(state || {})}
  <div class="pricesec">
    ${discount && discount.amount > 0 ?`
    <table style="width:100%;border-collapse:collapse;font-size:12px;margin-bottom:14px;">
      <tr><td style="padding:3px 0;color:#5a6a7a;">Quote total</td><td style="padding:3px 0;text-align:right;color:#5a6a7a;">${fmtGBP(discount.subtotalExVat)}</td></tr>
      <tr><td style="padding:3px 0;color:#5a6a7a;">${discount.reason || "Discount"}${/%$/.test(discount.label || "") ? ` (${discount.label})` : ""}</td><td style="padding:3px 0 3px 12px;text-align:right;color:#b23b3b;white-space:nowrap;vertical-align:top;">−${fmtGBP(discount.amount)}</td></tr>
    </table>
    `:""}
    <div class="pricemain">
      <div>
        <div class="pricelabel">Total ex VAT</div>
        <div class="pricefig">${fmtGBP(total)}</div>
        <table style="border-collapse:collapse;font-size:12px;margin-top:6px;">
          <tr><td style="padding:2px 14px 2px 0;color:#5a6a7a;">VAT (20%)</td><td style="padding:2px 0;text-align:right;color:#5a6a7a;">${fmtGBP(total*0.2)}</td></tr>
          <tr><td style="padding:2px 14px 2px 0;font-weight:700;color:#0e1520;">Total inc VAT</td><td style="padding:2px 0;text-align:right;font-weight:700;color:#0e1520;">${fmtGBP(incVat)}</td></tr>
        </table>
      </div>
      <div class="priceterms">
        <p>50% deposit (${fmtGBP(incVat / 2)} inc VAT) secures the booking</p>
        <p>${inUnit ? "Balance payable on collection" : "Balance payable on completion"}</p>
      </div>
    </div>
    ${needsShowcasePhotoLine(discount) ? `<p style="font-size:11px;color:#5a6a7a;margin-top:10px;">${SHOWCASE_PHOTO_LINE}</p>` : ""}
    ${notes.exclusions?`<div style="margin-top:16px;padding-top:12px;border-top:1px solid #eef0f5;font-size:11px;color:#8a95a3;"><strong>Excludes:</strong> ${notes.exclusions}</div>`:""}
  </div>
  <!-- Page 2 (print): the full detail behind page 1. -->
  <div class="p2">
  <div class="detail">
  ${autoScopeHTML(state || {}, clientScope)}
  <div class="sec">
    <div class="sectitle">What's Included</div>
    <table class="included">
      <tr><td>• All operative time and labour</td></tr>
      <tr><td>• Dry ice and all consumables</td></tr>
      ${protLabel?`<tr><td>• All specified protection materials</td></tr>`:""}
      <tr><td>• ${inUnit ? "Vehicle on a lift for full underside access" : "Professional dry ice blasting equipment"}</td></tr>
      <tr><td>• Pre and post condition photography</td></tr>
    </table>
  </div>
  </div>
  </div>
  <p class="tagline" style="font-size:10px;font-weight:700;color:#1D3461;letter-spacing:0.18em;text-transform:uppercase;margin:24px 0 8px;">We don’t just clean vehicles. We preserve them.</p>
  <div class="terms">
    <strong style="color:#5a6a7a">Terms &amp; Conditions</strong><br/>
    Dry ice blasting is a non-abrasive, moisture-free and chemical-free process using solid CO₂ pellets. Work is carried out by trained operatives. Public liability insurance held; certificates available on request. All prices subject to VAT at the prevailing rate.${protectionTermsSentence(protection)} Valid 30 days from date of issue.
  </div>
  <!-- The one footer: company details, at the end of the last page. -->
  <div class="footer">${legalFooterLine(company)}</div>
</div>
</body>
</html>`;
}

// ─── Build the quote HTML from a logged row (as returned by fetchAllQuotes) ──
export async function generateQuoteDocumentHTML(quote) {
  const quoteId = quote["Quote Number"];
  const stateJson = quote["State JSON"] ? JSON.parse(quote["State JSON"]) : null;
  if (!stateJson) throw new Error("Quote data not available — nothing saved to regenerate from.");

  const totalExVat = parseFloat((quote["Total (ex VAT)"] || "0").replace(/[^0-9.]/g, "")) || 0;
  const isAuto = quote["Category"] === "Automotive";
  const discount = resolveDiscount(stateJson.commercial, totalExVat);
  const dateStr = quoteDateStr(quote["Date Created"]);
  const company = await loadCompanySettings();

  if (isAuto) {
    return buildAutomotiveQuoteHTML({
      refNo: quoteId || stateJson.job?.quoteRef,
      dateStr,
      totalExVat,
      discount,
      state: stateJson,
      logoSrc: `${window.location.origin}/logo-dark.png`,
      company,
    });
  }

  const { job, service, profile, deploy, notes } = stateJson;
  return await generateQuoteHTML({
    // Same as above: the row's number, not State JSON's possibly-provisional one.
    job: { ...job, quoteRef: quoteId || job?.quoteRef },
    service, profile, deploy, notes,
    finalPrice: totalExVat,
    dateStr,
    subtotalExVat: discount ? discount.subtotalExVat : totalExVat,
    discount: discount ? { amount: discount.amount, label: discount.label, reason: discount.reason } : null,
    company,
  });
}
